import { useEffect, useState } from 'react';
import { useInteraction } from 'src/common/hooks/useInteraction';
import { useCoverSheetDocument } from 'src/views/Coversheet/hooks/useCoverSheetDocument';
import { useSetMissingFields } from 'src/views/Coversheet/state/coverSheetValidation';
import { computeMissingFields } from 'src/views/Coversheet/utils/computeMissingFields';
import type { InteractionType } from '@/common/types/interaction';
import {
  computeInlineContactMissingFields,
  getContactSectionByType,
  mergeContactMissingFields,
  stripContactCards,
} from '@/views/Contacts/inline/inlineMissingFields';
import { hydrateContacts } from '@/views/Contacts/shared/hydrate';
import type { ContactType } from '@/views/Contacts/shared/types';
import { useContactsData } from '@/views/Contacts/shared/useContactsData';
import { useCoverSheetConfig } from '../useCoverSheetConfig';

// Contact types the section renderer suppresses on a client-group offer.
const CLIENT_GROUP_EXCLUDED_TYPES: ContactType[] = ['agent', 'client'];

export function useInitializeCoverSheetSideBar(
  interactionId: string,
  interactionType: InteractionType,
  skip = false,
) {
  const { data: interaction, isLoading: interactionLoading } = useInteraction(
    interactionId,
    interactionType,
    skip,
  );
  const leaseRepresentation = interaction?.representation ?? null;
  const { data: configData } = useCoverSheetConfig({
    interactionId,
    interactionType,
    representationType: leaseRepresentation,
    skip: skip || interactionLoading,
  });
  const coverSheetConfig = configData?.coverSheetConfig;
  const { data: coverSheetData } = useCoverSheetDocument(
    interactionId,
    interactionType,
    configData,
  );

  // Inline contact cards validate through the contacts form rather than the
  // config, so their required fields are derived from the contacts data here.
  const { data: contacts } = useContactsData(interactionId, skip);
  const hasClientGroup = Boolean(interaction?.clientGroupId);
  const rawState = interaction?.property?.state;

  const [initialized, setInitialized] = useState(false);
  const setMissingFields = useSetMissingFields(interactionId);

  useEffect(() => {
    // If skip is ever true, it's because we were on the Cover Sheet page, which
    // handles initialization itself. Once done, this never needs to re-initialize
    // even when navigating away from the Cover Sheet page.
    if (skip) setInitialized(true);
  }, [skip]);

  useEffect(() => {
    if (!coverSheetConfig || !coverSheetData || skip || initialized) return;
    setInitialized(true);
  }, [coverSheetConfig, coverSheetData, skip, initialized]);

  // Keep the missing-fields atom in sync from data so the Documents tab is
  // blocked / sidebar badges show even when the user lands directly on a
  // non-Cover-Sheet page (e.g. /cover-letter) without ever mounting the
  // per-field validators.
  useEffect(() => {
    if (skip || !coverSheetConfig || !coverSheetData) return;

    const sectionByType = getContactSectionByType(coverSheetConfig, {
      excludeTypes: hasClientGroup ? CLIENT_GROUP_EXCLUDED_TYPES : [],
    });
    const contactFields = contacts
      ? computeInlineContactMissingFields(hydrateContacts(contacts), {
          interactionType,
          rawState,
          representation: leaseRepresentation,
          sectionByType,
        })
      : [];
    setMissingFields(
      mergeContactMissingFields(
        computeMissingFields(
          stripContactCards(coverSheetConfig),
          coverSheetData,
        ),
        contactFields,
      ),
    );
  }, [
    skip,
    coverSheetConfig,
    coverSheetData,
    setMissingFields,
    contacts,
    interactionType,
    rawState,
    leaseRepresentation,
    hasClientGroup,
  ]);
}
